// Promo Codes - every BigCommerce promotion, in plain English, for customer service.
// Rachael, Oct 21, 2026: "Could we get a spot in CPX that shows us current, active codes?
// Also what the requirements are - order minimum, VIP ok, non-VIPs only, specific products only."
// Data and the rule translation: netlify/functions/promo-codes.js (live from BigCommerce).
import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { getAccessToken } from '../services/auth';

const fmt = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const STATE = {
  live: { title: 'Live now', color: '#2e7d32' },
  upcoming: { title: 'Coming up', color: '#003b5c' },
  ended: { title: 'Ended in the last 30 days', color: '#888' },
};

async function load() {
  const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const headers = {};
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base + '/promo-codes', { headers });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || 'promo-codes ' + res.status);
  return j;
}

function Row({ k, v, strong }) {
  if (!v) return null;
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 13, padding: '3px 0' }}>
      <div style={{ width: 110, flex: '0 0 110px', color: 'var(--gray-500, #777)' }}>{k}</div>
      <div style={{ fontWeight: strong ? 700 : 400 }}>{v}</div>
    </div>
  );
}

function Card({ p }) {
  const codeText = p.automatic ? 'Automatic, no code'
    : p.codes && p.codes.kind === 'shared' ? p.codes.code
      : p.codes ? `${(p.codes.count || 0).toLocaleString()} one-use codes${p.codes.prefix ? ', starting ' + p.codes.prefix : ''}` : '';
  const vipColor = /only/.test(p.who) ? '#b8860b' : /Not VIP/.test(p.who) ? '#dc582a' : '#2e7d32';
  return (
    <div style={{ background: '#fff', border: '1px solid #e3e8ee', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <div style={{ fontWeight: 800, color: 'var(--navy, #003b5c)', fontSize: 15 }}>{p.name}</div>
        <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, background: '#f3f6f9', padding: '2px 8px', borderRadius: 4 }}>{codeText}</div>
      </div>
      <div style={{ marginTop: 6 }}>
        <Row k="Gives" v={p.gives} strong />
        <Row k="Minimum" v={p.needs} />
        <Row k="VIP members" v={<span style={{ color: vipColor, fontWeight: 700 }}>{p.who}</span>} />
        <Row k="Also" v={p.repeat} />
        <Row k="Sent to / for" v={p.campaign} />
        <Row k="Dates" v={`${fmt(p.starts)} to ${p.ends ? fmt(p.ends) : 'no end date'}`} />
        <Row k="Used" v={`${(p.uses || 0).toLocaleString()} time${p.uses === 1 ? '' : 's'}${p.maxUses ? ` of ${p.maxUses} allowed` : ''}`} />
      </div>
    </div>
  );
}

// "Who was sent what code" (Rachael, Oct 22, 2026): the one-use code on a customer's email
// contact, whether it has been used, and whether the newsletter actually went to them.
function CodeLookup() {
  const [email, setEmail] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const go = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setErr(null); setRes(null);
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
      const headers = {};
      const token = getAccessToken();
      if (token) headers.Authorization = 'Bearer ' + token;
      const r = await fetch(base + '/promo-codes?email=' + encodeURIComponent(email.trim()), { headers });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'lookup ' + r.status);
      setRes(j);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  const when = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
  return (
    <div style={{ background: '#f7f9fb', border: '1px solid #e3e8ee', borderRadius: 8, padding: '12px 14px', marginBottom: 18 }}>
      <div style={{ fontWeight: 800, color: 'var(--navy, #003b5c)', marginBottom: 6 }}>Look up a customer's code</div>
      <form onSubmit={go} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer email"
          style={{ flex: '1 1 260px', padding: '6px 10px', border: '1px solid #ccd5de', borderRadius: 4 }} />
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Looking' : 'Look up'}</button>
      </form>
      {err && <div style={{ color: '#b42318', marginTop: 8 }}>{err}</div>}
      {res && (
        <div style={{ marginTop: 10, fontSize: 13 }}>
          {!res.inDotdigital ? (
            <div><b>{res.email}</b> is not on our email list, so no newsletter or code went to that address.</div>
          ) : (
            <>
              <Row k="Email list" v={res.status === 'Subscribed' ? 'Subscribed' : `${res.status}: newsletters do not go to this address`} strong={res.status !== 'Subscribed'} />
              <Row k="Their code" v={res.code ? <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{res.code}</span> : 'No one-use code on file'} />
              {res.code && <Row k="For" v={res.promotion || 'not a live promotion any more'} />}
              {res.code && res.codeUsed !== null && <Row k="Used yet" v={res.codeUsed ? 'Yes, already used' : 'No, still good'} />}
              {(res.campaigns || []).map((c) => (
                <Row key={c.name} k="Newsletter" v={`${c.name}: ${c.sent ? `sent ${when(c.sentAt)}${c.opened ? ', opened' : ''}` : 'NOT sent to them'}`} />
              ))}
            </>
          )}
          <div style={{ color: '#888', marginTop: 6 }}>The code shown is from the latest one-use-code newsletter; each new one replaces it.</div>
        </div>
      )}
    </div>
  );
}

export default function PromoCodes() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(async () => {
    setBusy(true); setErr(null);
    try { setData(await load()); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const groups = ['live', 'upcoming', 'ended'].map((s) => ({ s, rows: ((data && data.promos) || []).filter((p) => p.state === s) }));
  return (
    <div style={{ padding: 20, maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <h2 style={{ margin: 0, color: 'var(--navy, #003b5c)' }}>Promo Codes</h2>
        <button onClick={refresh} disabled={busy} className="btn btn-secondary" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <RefreshCw size={14} /> {busy ? 'Loading' : 'Refresh'}
        </button>
      </div>
      <p style={{ fontSize: 13, color: '#555', margin: '8px 0 16px' }}>
        Straight from BigCommerce, so this is exactly what checkout does. <b>VIP members</b> is what checkout enforces.
        A code sent only to non-VIPs can still work for a VIP who has it, because we never block a VIP at checkout:
        if a VIP asks about a non-VIP code, check <b>Sent to / for</b> before promising it.
      </p>
      <CodeLookup />
      {err && <div style={{ color: '#b42318', marginBottom: 12 }}>{err}</div>}
      {!data && !err && <div>Loading...</div>}
      {data && groups.map((g) => (
        <div key={g.s} style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 800, color: STATE[g.s].color, margin: '6px 0 8px' }}>{STATE[g.s].title} ({g.rows.length})</div>
          {g.rows.length ? g.rows.map((p) => <Card key={p.id} p={p} />) : <div style={{ fontSize: 13, color: '#888' }}>None.</div>}
        </div>
      ))}
    </div>
  );
}
